import React, { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Package, Topic, VocabularyWord } from '../types';
import {
  fetchPackages,
  fetchTopicsByPackageId,
  fetchVocabulariesByTopicId,
  deletePackage,
  deleteTopic,
  deleteVocabulary,
  createVocabulariesBatch
} from '../api/api';

// Import các component con
import PackageListPanel from '../components/studio/PackageListPanel';
import TopicListPanel from '../components/studio/TopicListPanel';
import VocabularyListPanel from '../components/studio/VocabularyListPanel';
import Modal from '../components/Modal';
import PackageForm from '../components/studio/PackageForm';
import TopicForm from '../components/studio/TopicForm';
import VocabularyForm from '../components/studio/VocabularyForm';

const CreationStudioPage: React.FC = () => {
  // --- STATE DỮ LIỆU ---
  const [packages, setPackages] = useState<Package[]>([]);
  const [topics, setTopics] = useState<Topic[]>([]);
  const [vocabularies, setVocabularies] = useState<VocabularyWord[]>([]);

  // --- STATE LỰA CHỌN & TẢI DỮ LIỆU ---
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  const [isLoadingPackages, setIsLoadingPackages] = useState(true);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [isLoadingVocabs, setIsLoadingVocabs] = useState(false);

  // --- STATE CHO MODAL & EDIT ---
  const [modalContent, setModalContent] = useState<'PACKAGE' | 'TOPIC' | 'VOCAB' | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);

  // --- STATE CHO TÍNH NĂNG IMPORT ---
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [parsedVocabs, setParsedVocabs] = useState<Omit<VocabularyWord, 'id'>[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- HÀM TẢI DỮ LIỆU ---
  const loadPackages = async () => {
    setIsLoadingPackages(true);
    setPackages(await fetchPackages());
    setIsLoadingPackages(false);
  };

  const loadTopicsForPackage = async (packageId: string) => {
    setIsLoadingTopics(true);
    setTopics(await fetchTopicsByPackageId(packageId));
    setIsLoadingTopics(false);
  };

  const loadVocabsForTopic = async (topicId: string) => {
    setIsLoadingVocabs(true);
    setVocabularies(await fetchVocabulariesByTopicId(topicId));
    setIsLoadingVocabs(false);
  };

  useEffect(() => {
    loadPackages();
  }, []);

  // --- HÀM XỬ LÝ SỰ KIỆN ---
  const handleSelectPackage = (pkg: Package) => {
    if (pkg.id === selectedPackageId) return;
    setSelectedPackageId(pkg.id);
    setSelectedTopicId(null);
    setVocabularies([]);
    loadTopicsForPackage(pkg.id);
  };

  const handleSelectTopic = (topic: Topic) => {
    if (topic.id === selectedTopicId) return;
    setSelectedTopicId(topic.id);
    loadVocabsForTopic(topic.id);
  };

  // --- HÀM QUẢN LÝ MODAL ---
  const handleOpenModal = (type: 'PACKAGE' | 'TOPIC' | 'VOCAB', itemToEdit: any = null) => {
    setEditingItem(itemToEdit);
    setModalContent(type);
  };

  const handleCloseModal = () => {
    setModalContent(null);
    setEditingItem(null);
  };

  const handleSaveSuccess = async (newItem?: any) => {
    const currentModal = modalContent;
    handleCloseModal();
    // if (currentModal === 'PACKAGE') loadPackages();
    // if (currentModal === 'TOPIC' && selectedPackageId) loadTopicsForPackage(selectedPackageId);
    if (currentModal === 'PACKAGE') {
      await loadPackages();
      if (newItem?.id) {
        setSelectedPackageId(newItem.id);
        loadTopicsForPackage(newItem.id);
      }
    }

    if (currentModal === 'TOPIC' && selectedPackageId) {
      await loadTopicsForPackage(selectedPackageId);
      if (newItem?.id) {
        setSelectedTopicId(newItem.id);
        loadVocabsForTopic(newItem.id);
      }
    }
    if (currentModal === 'VOCAB' && selectedTopicId) loadVocabsForTopic(selectedTopicId);
  };

  // --- HÀM XỬ LÝ XÓA ---
  const handleDeletePackage = async (packageId: string) => {
    if (window.confirm('Are you sure you want to delete this package and all its contents?')) {
      try {
        await deletePackage(packageId);
        setPackages(prev => prev.filter(p => p.id !== packageId));
        if (selectedPackageId === packageId) {
          setSelectedPackageId(null);
          setSelectedTopicId(null);
          setTopics([]);
          setVocabularies([]);
        }
      } catch (error) { console.error("Failed to delete package:", error); }
    }
  };

  const handleDeleteTopic = async (topicId: string) => {
    if (window.confirm('Are you sure you want to delete this topic?')) {
      try {
        await deleteTopic(topicId);
        setTopics(prev => prev.filter(t => t.id !== topicId));
        if (selectedTopicId === topicId) {
          setSelectedTopicId(null);
          setVocabularies([]);
        }
      } catch (error) { console.error("Failed to delete topic:", error); }
    }
  };

  const handleDeleteVocabulary = async (vocabId: string) => {
    if (window.confirm('Are you sure you want to delete this word?')) {
      try {
        await deleteVocabulary(vocabId);
        setVocabularies(prev => prev.filter(v => v.id !== vocabId));
      } catch (error) { console.error("Failed to delete vocabulary:", error); }
    }
  };

  // --- HÀM XỬ LÝ IMPORT ---
  const handleFileParsed = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedTopicId) {
      alert("Please select a topic before importing a file.");
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json: any[] = XLSX.utils.sheet_to_json(worksheet);

        // --- THAY ĐỔI CHÍNH Ở ĐÂY ---
        // Thay vì dùng spread, chúng ta tạo đối tượng mới một cách tường minh
        const vocabsWithTopicId = json.map(row => ({
          word: row.word || '',
          definition: row.definition || '',
          type: row.type || 'noun',
          ipa: row.ipa || '',
          example1: row.example1 || '',
          example2: row.example2 || '',
          audioUrl: '',
          imgUrl: '',
          topicName: '',
          topicId: selectedTopicId!, // Thêm ID của topic đang được chọn
        }));

        setParsedVocabs(vocabsWithTopicId);
        setIsImportModalOpen(true);
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        alert("There was an error reading the file. Please ensure it's a valid Excel file.");
      }
    };
    reader.readAsArrayBuffer(file);
    event.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!selectedTopicId || parsedVocabs.length === 0) return;
    setIsImporting(true);
    try {
      console.log("parsedVocabs", parsedVocabs);
      await createVocabulariesBatch(parsedVocabs);
      setIsImportModalOpen(false);
      setParsedVocabs([]);
      loadVocabsForTopic(selectedTopicId);
    } catch (error) {
      console.error("Failed to import vocabularies:", error);
      alert("Import failed.");
    } finally {
      setIsImporting(false);
    }
  };

  const selectedPackage = packages.find(p => p.id === selectedPackageId) || null;
  const selectedTopic = topics.find(t => t.id === selectedTopicId) || null;

  return (
    <>
      <input type="file" ref={fileInputRef} onChange={handleFileParsed} style={{ display: 'none' }} accept=".xlsx, .xls" />
      <div className="creation-studio glass-effect">
        <div className="studio-panel">
          <PackageListPanel
            isLoading={isLoadingPackages} packages={packages} selectedPackageId={selectedPackageId}
            onSelect={handleSelectPackage} onAdd={() => handleOpenModal('PACKAGE')} onEdit={(pkg) => handleOpenModal('PACKAGE', pkg)} onDelete={handleDeletePackage}
          />
        </div>
        <div className="studio-panel">
          <TopicListPanel
            isLoading={isLoadingTopics} selectedPackage={selectedPackage} topics={topics} selectedTopicId={selectedTopicId}
            onSelect={handleSelectTopic} onAdd={() => handleOpenModal('TOPIC')} onEdit={(topic) => handleOpenModal('TOPIC', topic)} onDelete={handleDeleteTopic}
          />
        </div>
        <div className="studio-panel">
          <VocabularyListPanel
            isLoading={isLoadingVocabs} vocabularies={vocabularies} selectedTopic={selectedTopic}
            onOpenAddModal={() => handleOpenModal('VOCAB')} onEdit={(vocab) => handleOpenModal('VOCAB', vocab)} onDelete={handleDeleteVocabulary}
            onOpenImportModal={() => fileInputRef.current?.click()}
            isAddDisabled={!selectedTopic}
          />
        </div>
      </div>

      {/* Modal chính cho Create/Edit */}
      <Modal isOpen={!!modalContent} onClose={handleCloseModal} title={`${editingItem ? 'Edit' : 'Create New'} ${modalContent}`}>
        {modalContent === 'PACKAGE' && <PackageForm onSuccess={handleSaveSuccess} initialData={editingItem as Package} />}
        {modalContent === 'TOPIC' && selectedPackageId && <TopicForm onSuccess={handleSaveSuccess} initialData={editingItem as Topic} packageId={selectedPackageId} />}
        {modalContent === 'VOCAB' && selectedTopicId && <VocabularyForm onSuccess={handleSaveSuccess} initialData={editingItem as VocabularyWord} topicId={selectedTopicId} />}
      </Modal>

      {/* Modal cho Import Excel */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        title={`Confirm Import (${parsedVocabs.length} words)`}
      >
        <div>
          <p>Please review the words below before importing:</p>

          {/* --- THAY ĐỔI BÊN TRONG THẺ ul NÀY --- */}
          <ul className="preview-list">
            {parsedVocabs.slice(0, 10).map((vocab, index) => ( // Hiển thị tối đa 10 từ
              <li key={index} className="preview-item">
                <p className="preview-item-header">
                  <strong>{vocab.word}</strong>
                  <span className="preview-item-type">{vocab.type}</span>
                </p>
                <p className="preview-item-ipa">{vocab.ipa}</p>
                <p className="preview-item-def">{vocab.definition}</p>
                {vocab.example1 && <p className="preview-item-ex"><em>Ex: "{vocab.example1}"</em></p>}
              </li>
            ))}
          </ul>

          {parsedVocabs.length > 10 && <p>...and {parsedVocabs.length - 10} more.</p>}
          <button className="btn btn-primary" onClick={handleConfirmImport} disabled={isImporting}>
            {isImporting ? 'Importing...' : 'Confirm & Save'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default CreationStudioPage;